/**
 * Backend API client.
 *
 * Responsibilities:
 *   1. Build fully-qualified URLs from `env.VITE_API_BASE_URL`.
 *   2. Attach the Telegram initData in the `x-telegram-init-data` header
 *      (matches src/api/middleware/auth.ts). NOT the `Authorization: tma`
 *      scheme; that header name is wrong - the backend reads a custom
 *      header.
 *   3. Walk outgoing JSON bodies and stringify any bigint values before
 *      `JSON.stringify` trips over them. The backend's Zod schemas use
 *      `z.coerce.bigint()` which accepts the string form.
 *   4. Parse `{ error: { code, message, details } }` envelopes from
 *      non-2xx responses and throw a typed `ApiError`.
 *
 * This module intentionally has no knowledge of specific endpoints; each
 * resource module (`deals.ts`, `disputes.ts`, `users.ts`) imports
 * `request<T>` and exports typed functions.
 */

import { env } from '@/config/env';
import { getInitData } from '@/lib/telegram/initData';
import { ApiError, type ApiErrorCode } from './errors';

/**
 * Query string shape.
 *
 * Using a readonly record of `unknown` keys here would force every
 * callsite into an index-signature-compatible object; instead we accept
 * any plain object whose values are primitives or undefined and coerce
 * to string inside `buildUrl`. `QueryInput` is intentionally wide so
 * typed resource modules can pass their own interface types directly.
 */
export type QueryInput = {
  readonly [key: string]: string | number | boolean | undefined;
};

export interface RequestOptions {
  query?: QueryInput;
  /** Abort handle so callers can cancel in-flight requests. */
  signal?: AbortSignal;
  /** Per-request override for the init-data header (tests). */
  initData?: string;
}

const KNOWN_ERROR_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'deal_not_found',
  'unauthorized_actor',
  'invalid_state_transition',
  'idempotency_conflict',
  'blockchain_error',
  'validation_error',
  'non_custodial_violation',
  'rate_limited',
  'network_error',
  'unknown_error',
]);

function toErrorCode(raw: unknown): ApiErrorCode {
  if (typeof raw === 'string' && KNOWN_ERROR_CODES.has(raw as ApiErrorCode)) {
    return raw as ApiErrorCode;
  }
  return 'unknown_error';
}

/**
 * Walk any JSON-able value and replace bigint with its decimal string
 * representation. Leaves everything else untouched, including Date
 * objects (they serialise naturally via `toJSON`).
 */
function normalizeBody(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'bigint') return value.toString();
  if (Array.isArray(value)) return value.map(normalizeBody);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      out[key] = normalizeBody(v);
    }
    return out;
  }
  return value;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = env.VITE_API_BASE_URL;
  const normalised = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${normalised}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function parseErrorResponse(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON body (HTML error page, empty 502 etc.) - swallow and
    // surface a generic message below.
  }

  if (body && typeof body === 'object' && 'error' in body) {
    const err = (body as { error?: { code?: unknown; message?: unknown; details?: unknown } })
      .error;
    if (err && typeof err === 'object') {
      return new ApiError(
        toErrorCode(err.code),
        typeof err.message === 'string' ? err.message : `HTTP ${response.status}`,
        response.status,
        err.details,
      );
    }
  }

  if (response.status === 429) {
    return new ApiError('rate_limited', 'Too many requests', 429);
  }

  return new ApiError(
    'unknown_error',
    `HTTP ${response.status} ${response.statusText || ''}`.trim(),
    response.status,
    body,
  );
}

/**
 * Low-level request primitive. Every resource module builds on this.
 *
 * Generic parameter `T` is the parsed JSON response. Use `void` to ignore
 * the body (e.g. 204 No Content endpoints - the function will resolve
 * with `undefined` in that case).
 */
export async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const initData = options.initData ?? getInitData();

  const headers: Record<string, string> = {
    'x-request-id': crypto.randomUUID(),
    accept: 'application/json',
  };

  // In dev mode without Telegram initData, send the dev bypass header
  // so the backend's dev-only auth shortcut kicks in.
  if (initData.length > 0) {
    headers['x-telegram-init-data'] = initData;
  } else if (import.meta.env.DEV) {
    headers['x-dev-telegram-id'] = '890440818';
  }

  let payload: string | undefined;
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(normalizeBody(body));
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: payload,
      credentials: 'omit',
      signal: options.signal,
    });
  } catch (err) {
    // Network error - fetch throws `TypeError` on DNS/offline/etc.
    throw new ApiError(
      'network_error',
      err instanceof Error ? err.message : 'Network error',
      0,
    );
  }

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  // 204 / empty body - resolve with undefined.
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (text.length === 0) {
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch (err) {
    throw new ApiError(
      'unknown_error',
      `Invalid JSON response: ${err instanceof Error ? err.message : 'parse error'}`,
      response.status,
    );
  }
}

/** Convenience wrappers. */
export const apiGet = <T>(path: string, options?: RequestOptions): Promise<T> =>
  request<T>('GET', path, undefined, options);

export const apiPost = <T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> => request<T>('POST', path, body, options);

export const apiPatch = <T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> => request<T>('PATCH', path, body, options);

export const apiDelete = <T>(
  path: string,
  options?: RequestOptions,
): Promise<T> => request<T>('DELETE', path, undefined, options);
